import { inject, Injectable, signal } from '@angular/core';
import { AnalyticsEvent, AnalyticsService, ApiCallingV2Service, API_CONFIG } from '@zayan-erp/shared';
import { defaultIfEmpty, filter, map, Observable, Subject, switchMap, take, tap, timer } from 'rxjs';
import {
  ApsDealDocument,
  DealDocumentTemplateState,
  EnvelopeStatus,
  RecipientStatus,
  DealAmendmentUploadDraft,
  DealDocumentSignatureFlowType,
  GenerateContractRequest,
  ExternalDocReq,
  ExternalDocument,
  DocuSignInPersonSigningSession
} from '../models/deal-document.models';
import { IVoidContractReason } from '../models/voidcontract.model';
import { DealDocumentHelper } from '../helpers/deal-document.helper';
import { CustomDocumentRequest, CustomDocumentSigner } from '../models/custom-documment.models';
import { SalesApsService } from './sales-aps.service';

@Injectable({
  providedIn: 'root'
})
export class DealDocumentsService {
  #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;
  #http = inject(ApiCallingV2Service);
  #analytics = inject(AnalyticsService);
  #salesAps = inject(SalesApsService);

  private dealDocumentTemplateState = signal<Record<string, DealDocumentTemplateState>>({});
  private dealAmendmentUploadDraft = signal<Record<string, DealAmendmentUploadDraft | null>>({});
  private dealNonApsDocumentsCache = signal<Record<string, ApsDealDocument[]>>({});
  private readonly dealNonApsDocumentsStorageKey = 'deal-non-aps-documents';
  openUploadExternalContractModal = new Subject<void>();
  openUploadExternalContractModal$ = this.openUploadExternalContractModal.asObservable();

  requestOpenUploadExternalContractModal(): void {
    this.openUploadExternalContractModal.next();
  }

  voidDealContract(dealId: string, payload: { voidReason: string }, buttonId: string): Observable<any> {
    return this.#http
      .postData(
        `${this.baseUrl}/deal-documents/deal/${dealId}/void-contract`,
        {
          showLoader: true,
          buttonId,
          showMessage: true,
          message: 'Contract voided successfully'
        },
        payload
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealContractVoided, {
            ...this.#salesAps.dealCtx(dealId),
            void_reason: payload.voidReason
          });
        })
      );
  }

  getDealDocumentTemplateState(dealId: string): DealDocumentTemplateState | null {
    return this.dealDocumentTemplateState()[dealId] ?? null;
  }

  setDealDocumentTemplateState(dealId: string, state: DealDocumentTemplateState): void {
    this.dealDocumentTemplateState.update(currentState => ({
      ...currentState,
      [dealId]: state
    }));
  }

  clearDealDocumentTemplateState(dealId: string): void {
    this.dealDocumentTemplateState.update(currentState => {
      if (!(dealId in currentState)) {
        return currentState;
      }

      const { [dealId]: _removedState, ...rest } = currentState;
      return rest;
    });
  }

  clearAllDealDocumentTemplateState(): void {
    this.dealDocumentTemplateState.set({});
  }

  getDealAmendmentUploadDraft(dealId: string): DealAmendmentUploadDraft | null {
    return this.dealAmendmentUploadDraft()[dealId] ?? null;
  }

  setDealAmendmentUploadDraft(dealId: string, draft: DealAmendmentUploadDraft | null): void {
    this.dealAmendmentUploadDraft.update(currentState => ({
      ...currentState,
      [dealId]: draft
    }));
  }

  clearDealAmendmentUploadDraft(dealId: string): void {
    this.setDealAmendmentUploadDraft(dealId, null);
  }

  clearAllDealAmendmentUploadDrafts(): void {
    this.dealAmendmentUploadDraft.set({});
  }

  cacheDealNonApsDocument(dealId: string, document: ApsDealDocument): void {
    if (!dealId || !document?.id) {
      return;
    }
    this.mergeAndPersistDealNonApsDocuments(dealId, [document]);
  }

  applySigningInitiatedInCache(dealId: string, documentId: string, signingType: DealDocumentSignatureFlowType): void {
    if (!dealId || !documentId) {
      return;
    }

    const cached = this.getDealNonApsDocumentsCache(dealId);
    const updated = cached.map(doc => {
      if (doc.id !== documentId) {
        return doc;
      }

      const nextSignatures = doc.dealDocumentSignatures.map(sig => {
        if (sig.entityType?.toLowerCase() === 'user') {
          return sig;
        }
        if (sig.status === RecipientStatus.Complete) {
          return sig;
        }
        return { ...sig, status: RecipientStatus.Sent };
      });

      return {
        ...doc,
        signingType,
        status: EnvelopeStatus.Sent,
        dealDocumentSignatures: nextSignatures
      };
    });

    this.persistDealNonApsDocumentsCache(dealId, updated);
  }

  private findDealIdForNonApsDocument(documentId: string): string | undefined {
    for (const [dealId, docs] of Object.entries(this.dealNonApsDocumentsCache())) {
      if (docs.some(doc => doc.id === documentId)) {
        return dealId;
      }
    }

    const prefix = `${this.dealNonApsDocumentsStorageKey}:`;
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key?.startsWith(prefix)) {
        continue;
      }
      try {
        const dealId = key.slice(prefix.length);
        const parsed = JSON.parse(sessionStorage.getItem(key) ?? '[]') as ApsDealDocument[];
        if (Array.isArray(parsed) && parsed.some(doc => doc.id === documentId)) {
          return dealId;
        }
      } catch {
        // Ignore malformed cache entries
      }
    }

    return undefined;
  }

  applySignerCompleteInCache(
    dealId: string,
    documentId: string,
    signerId: string,
    completionSource: string | null = null
  ): void {
    if (!dealId || !documentId || !signerId) {
      return;
    }

    const cached = this.getDealNonApsDocumentsCache(dealId);
    const updated = cached.map(doc => {
      if (doc.id !== documentId) {
        return doc;
      }

      const nextSignatures = doc.dealDocumentSignatures.map(sig =>
        sig.id === signerId
          ? {
              ...sig,
              status: RecipientStatus.Complete,
              ...(completionSource ? { completionSource } : {})
            }
          : sig
      );
      const allSignersComplete =
        nextSignatures.length > 0 && nextSignatures.every(sig => sig.status === RecipientStatus.Complete);

      return {
        ...doc,
        dealDocumentSignatures: nextSignatures,
        status: allSignersComplete ? EnvelopeStatus.Complete : doc.status
      };
    });

    this.persistDealNonApsDocumentsCache(dealId, updated);
  }

  refreshDealNonApsDocumentsAfterSigning(
    dealId: string,
    documentId: string,
    signerId: string
  ): Observable<ApsDealDocument[]> {
    this.applySignerCompleteInCache(dealId, documentId, signerId);

    const prior = this.getDealNonApsDocumentsCache(dealId).find(doc => doc.id === documentId);
    const priorSignatureSnapshot = prior ? JSON.stringify(prior.dealDocumentSignatures) : '';

    return timer(0, 2500).pipe(
      take(8),
      switchMap(() => this.fetchNonApsDocumentsFromApi(dealId)),
      filter(docs => {
        const refreshed = docs.find(doc => doc.id === documentId);
        if (!refreshed) {
          return false;
        }
        return JSON.stringify(refreshed.dealDocumentSignatures) !== priorSignatureSnapshot;
      }),
      take(1),
      defaultIfEmpty(this.getDealNonApsDocumentsCache(dealId))
    );
  }

  removeDealNonApsDocumentFromCache(dealId: string, documentId: string): void {
    if (!dealId || !documentId) {
      return;
    }
    const cached = this.getDealNonApsDocumentsCache(dealId).filter(doc => doc.id !== documentId);
    this.persistDealNonApsDocumentsCache(dealId, cached);
  }

  removeDealNonApsDocumentsByDocumentTypeFromCache(dealId: string, documentType: string): void {
    if (!dealId || !documentType) {
      return;
    }
    const normalizedType = DealDocumentHelper.normalizeDealDocumentType(documentType);
    const cached = this.getDealNonApsDocumentsCache(dealId).filter(
      doc => DealDocumentHelper.normalizeDealDocumentType(doc.documentType) !== normalizedType
    );
    this.persistDealNonApsDocumentsCache(dealId, cached);
  }

  clearDealNonApsDocumentsCache(dealId: string): void {
    this.dealNonApsDocumentsCache.update(current => {
      if (!(dealId in current)) {
        return current;
      }
      const { [dealId]: _removed, ...rest } = current;
      return rest;
    });
    sessionStorage.removeItem(`${this.dealNonApsDocumentsStorageKey}:${dealId}`);
  }

  clearAllDealNonApsDocumentsCache(): void {
    this.dealNonApsDocumentsCache.set({});
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(`${this.dealNonApsDocumentsStorageKey}:`)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  }

  private mergeAndPersistDealNonApsDocuments(dealId: string, fromApi: ApsDealDocument[]): ApsDealDocument[] {
    const cached = this.getDealNonApsDocumentsCache(dealId);
    const merged = DealDocumentHelper.mergeDealNonApsDocuments(cached, fromApi);
    this.persistDealNonApsDocumentsCache(dealId, merged);
    return merged;
  }

  private getDealNonApsDocumentsCache(dealId: string): ApsDealDocument[] {
    const inMemory = this.dealNonApsDocumentsCache()[dealId];
    if (inMemory?.length) {
      return inMemory;
    }
    return this.readDealNonApsDocumentsCacheFromStorage(dealId);
  }

  private persistDealNonApsDocumentsCache(dealId: string, documents: ApsDealDocument[]): void {
    this.dealNonApsDocumentsCache.update(current => ({
      ...current,
      [dealId]: documents
    }));
    try {
      sessionStorage.setItem(`${this.dealNonApsDocumentsStorageKey}:${dealId}`, JSON.stringify(documents));
    } catch {
      // Ignore quota / private mode errors
    }
  }

  private readDealNonApsDocumentsCacheFromStorage(dealId: string): ApsDealDocument[] {
    try {
      const raw = sessionStorage.getItem(`${this.dealNonApsDocumentsStorageKey}:${dealId}`);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw) as ApsDealDocument | ApsDealDocument[];
      return Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
    } catch {
      return [];
    }
  }

  getContractVoidReason(): Observable<IVoidContractReason[]> {
    return this.#http.getData<IVoidContractReason[]>(`${this.baseUrl}/lookup/contract-void-reasons`, {
      showLoader: true,
      showMessage: false
    });
  }

  updateScheduledDateTime(dealId: string, payload: { scheduledAt: string; scheduleType: string }): Observable<any> {
    return this.#http.putData(`${this.baseUrl}/deal-schedules/${dealId}`, payload, {
      showLoader: true,
      showMessage: false,
      message: 'Scheduled date time updated successfully'
    });
  }

  getDealSchedule(dealId: string, showLoader = true): Observable<any> {
    return this.#http.getData<any>(`${this.baseUrl}/deal-schedules/${dealId}`, {
      showLoader,
      showMessage: false
    });
  }

  miscDocVoidSign(documentId: string, voidReason: string, buttonId: string): Observable<any> {
    return this.#http.postData(
      `${this.baseUrl}/deal-documents/${documentId}/void-contract/non-aps`,
      {
        showLoader: true,
        buttonId,
        showMessage: true
      },
      { voidReason: voidReason }
    );
  }

  regenerateNonApsContract(dealDocId: string, documentType: string, dealId: string): Observable<any> {
    return this.#http
      .postData(
        `${this.baseUrl}/deal-documents/${dealDocId}/reset/non-aps`,
        { showLoader: true, showMessage: true },
        {}
      )
      .pipe(tap(() => this.removeDealNonApsDocumentsByDocumentTypeFromCache(dealId, documentType)));
  }

  voidNonApsContract(dealDocumentId: string, voidReason: string): Observable<any> {
    return this.#http.postData(
      `${this.baseUrl}/deal-documents/${dealDocumentId}/void-contract/non-aps`,
      { showLoader: true, showMessage: true },
      { voidReason: voidReason }
    );
  }

  getDealNonApsDocuments(dealId: string): Observable<ApsDealDocument[]> {
    return this.fetchNonApsDocumentsFromApi(dealId, { showLoader: true });
  }

  private fetchNonApsDocumentsFromApi(
    dealId: string,
    options: { showLoader?: boolean } = {}
  ): Observable<ApsDealDocument[]> {
    return this.#http
      .getData<ApsDealDocument | ApsDealDocument[]>(`${this.baseUrl}/deal-documents/deal/${dealId}/non-aps`, {
        showLoader: options.showLoader ?? false,
        showMessage: false
      })
      .pipe(
        map(data => {
          const fromApi = Array.isArray(data) ? data : data ? [data] : [];
          return this.mergeAndPersistDealNonApsDocuments(dealId, fromApi);
        })
      );
  }

  generateNonApsDocument(dealId: string, documentType: string, buttonId: string): Observable<ApsDealDocument> {
    return this.#http
      .postData<ApsDealDocument>(
        `${this.baseUrl}/deal-documents/deal/${dealId}/non-aps-document/${documentType}`,
        {
          showLoader: true,
          buttonId,
          showMessage: false
        },
        {}
      )
      .pipe(tap(document => this.cacheDealNonApsDocument(dealId, document)));
  }

  generateAmendmentDocument(
    dealId: string,
    payload: GenerateContractRequest,
    buttonId: string
  ): Observable<ApsDealDocument> {
    return this.#http
      .postData<ApsDealDocument>(
        `${this.baseUrl}/deal-documents/deal/${dealId}/amendment-document`,
        { showLoader: true, buttonId, showMessage: true },
        payload
      )
      .pipe(
        tap(document => {
          this.#analytics.capture(AnalyticsEvent.AmendmentDocumentGenerated, this.#salesAps.dealCtx(dealId));
          this.cacheDealNonApsDocument(dealId, document);
        })
      );
  }

  resendDealDocument(dealDocumentId: string, dealId?: string): Observable<any> {
    return this.#http
      .postData(
        `${this.baseUrl}/deal-documents/${dealDocumentId}/resend`,
        {
          showLoader: true,
          showMessage: true,
          message: 'Document resent for signature'
        },
        {}
      )
      .pipe(
        tap(() => {
          if (dealId) {
            this.clearDealNonApsDocumentsCache(dealId);
          }
        })
      );
  }

  initiateDealInPersonSignature(
    documentId: string,
    flowType: DealDocumentSignatureFlowType,
    buttonId: string,
    dealId?: string
  ): Observable<any> {
    return this.#http
      .patchData(
        `${this.baseUrl}/deal-documents/${documentId}`,
        { signingType: flowType },
        { showLoader: true, buttonId, showMessage: false }
      )
      .pipe(
        tap(() => {
          const resolvedDealId = dealId ?? this.findDealIdForNonApsDocument(documentId);
          if (resolvedDealId) {
            this.applySigningInitiatedInCache(resolvedDealId, documentId, flowType);
          }
        })
      );
  }

  externalSignedDocument(dealId: string, payload: ExternalDocReq): Observable<ExternalDocument> {
    return this.#http
      .postData<ExternalDocument>(
        `${this.#apiConfig.baseUrlV2}/deals/${dealId}/mark-signed-externally`,
        { showLoader: true, showMessage: true, message: 'External contract uploaded successfully' },
        payload
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.ExternalSignedDocumentUploaded, this.#salesAps.dealCtx(dealId));
        })
      );
  }

  replaceExternalSignedDocument(dealId: string, payload: ExternalDocReq): Observable<ExternalDocument> {
    return this.#http
      .postData<ExternalDocument>(
        `${this.#apiConfig.baseUrlV2}/deals/${dealId}/replace-signed-externally-document`,
        { showLoader: true, showMessage: true, message: 'External contract replaced successfully' },
        payload
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.ExternalSignedDocumentReplaced, this.#salesAps.dealCtx(dealId));
        })
      );
  }

  generateDealContract(dealId: string, payload: GenerateContractRequest): Observable<any> {
    return this.#http
      .postData(
        `${this.baseUrl}/deal-documents/deal/${dealId}/contract`,
        { showLoader: true, buttonId: 'generate-contract', showMessage: true },
        payload
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealContractGenerated, this.#salesAps.dealCtx(dealId));
        })
      );
  }

  regenerateDealContract(dealId: string): Observable<any> {
    return this.#http
      .postData(
        `${this.baseUrl}/deal-documents/deal/${dealId}/reset-contract`,
        { showLoader: true, showMessage: true },
        {}
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealContractRegenerated, this.#salesAps.dealCtx(dealId));
        })
      );
  }

  sendToExecutive(dealDocumentId: string, buttonId: string): Observable<any> {
    return this.#http
      .postData(
        `${this.baseUrl}/deal-documents/${dealDocumentId}/send-to-executive`,
        {
          showLoader: true,
          buttonId,
          showMessage: true,
          message: 'Document sent to the executive for signing'
        },
        {}
      )
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx();
          if (ctx) {
            this.#analytics.capture(AnalyticsEvent.DocumentSentToExecutive, {
              ...ctx,
              deal_document_id: dealDocumentId
            });
          }
          this.applySendToExecutiveInCache(dealDocumentId);
        })
      );
  }

  startDocuSignInPersonSigning(
    dealDocumentId: string,
    signerId: string,
    signerName: string,
    returnUrl?: string,
    buttonId?: string
  ): Observable<DocuSignInPersonSigningSession> {
    const payload: any = { signerId, signerName };
    if (returnUrl) payload.returnUrl = returnUrl;

    return this.#http
      .postData<DocuSignInPersonSigningSession>(
        `${this.baseUrl}/deal-documents/${dealDocumentId}/docusign/in-person/start`,
        { showLoader: true, buttonId, showMessage: false },
        payload
      )
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx();
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.DocusignSigningStarted, {
            ...ctx,
            deal_document_id: dealDocumentId,
            signer_id: signerId,
            signer_name: signerName
          });
        })
      );
  }

  getDocuSignSignerSigningUrl(
    dealDocumentId: string,
    signerId: string,
    returnUrl: string
  ): Observable<DocuSignInPersonSigningSession> {
    return this.#http.postData<DocuSignInPersonSigningSession>(
      `${this.baseUrl}/deal-documents/${dealDocumentId}/docusign/signers/${signerId}/signing-url`,
      { showLoader: true, showMessage: false },
      { returnUrl }
    );
  }

  applySendToExecutiveInCache(dealDocumentId: string): void {
    if (!dealDocumentId) {
      return;
    }

    const dealId = this.findDealIdForNonApsDocument(dealDocumentId);
    if (!dealId) {
      return;
    }

    const cached = this.getDealNonApsDocumentsCache(dealId);
    const updated = cached.map(doc => {
      if (doc.id !== dealDocumentId) {
        return doc;
      }

      const nextSignatures = doc.dealDocumentSignatures.map(sig => {
        if (sig.entityType?.toLowerCase() === 'user' && sig.status === RecipientStatus.Pending) {
          return { ...sig, status: RecipientStatus.Sent };
        }
        return sig;
      });

      return {
        ...doc,
        dealDocumentSignatures: nextSignatures
      };
    });

    this.persistDealNonApsDocumentsCache(dealId, updated);
  }

  verifyCustomDocument(
    dealId: string,
    s3DocumentId: string,
    options: { showLoader?: boolean; showMessage?: boolean } = {}
  ): Observable<CustomDocumentSigner[]> {
    return this.#http.postData(
      `${this.baseUrl}/deal-documents/deal/${dealId}/custom-document/verify`,
      {
        showLoader: options.showLoader ?? true,
        showMessage: options.showMessage ?? true
      },
      { s3DocumentId: s3DocumentId }
    );
  }

  generateCustomDocument(dealId: string, payload: CustomDocumentRequest, buttonId: string): Observable<any> {
    return this.#http.postData(
      `${this.baseUrl}/deal-documents/deal/${dealId}/custom-document`,
      { showLoader: true, buttonId, showMessage: true },
      payload
    );
  }
}
